import {
    Component,
    Element,
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { selection } from "d3-selection";
import { arc, Arc, PieArcDatum } from "d3-shape";
import "d3-transition"; // required, else webpack will wont include the transition function
import { debounce } from "../../../utils/debounce";
import { getUniqueId } from "../../../utils/unique-id";
import { ChartMargin } from "../interfaces/chart-margin";
import { ChartState } from "../interfaces/chart-state";
import { GaugeChartArc } from "./gauge-chart-arc";
import { GaugeChartWrapper } from "./gauge-chart-wrapper";
import { arcInterpolator } from "../utils/interpolations";

export interface ChartDimensions {
    width: number;
    height: number;
    radius: number;
    arcFn: Arc<any, PieArcDatum<number>>;
    thresholdsArcFn: Arc<any, PieArcDatum<number>>;
    radiusMaxHeightFraction: number;
}

interface GaugeArc<T> extends PieArcDatum<T> {
    arcInterpolator: (
        arcFn: Arc<any, PieArcDatum<number>>,
        t: number
    ) => string;
}

/**
 * @slot value - The value to display in the middle of the arc.
 * @slot label - The label to display below the value.
 */
export interface Threshold {
    value: number;
    cssClass?: string;
    ignoreThresholdColor?: boolean;
}

@Component({
    tag: "uic-gauge-chart",
    styleUrl: "gauge-chart.scss",
    shadow: false,
})
export class GaugeChart {
    /** Label for the value. Displayed right below the chart, if set. */
    @Prop() label: string;

    /** The data prop for binding. Each key in the object is one arc with it's proportions calculated from the value, given as number */
    @Prop() value: number;

    /** The minimum value of the gauge. */
    @Prop() min: number = 0;

    /** The maximum value of the gauge. */
    @Prop() max: number = 100;

    /** Value formatter, set a custom format function for chart value. */
    @Prop() valueFormatter: (x: number, max: number) => string = (x, max) =>
        parseFloat(((x / max) * 100).toString()).toFixed(1) + "%";

    /** Define thresholds. They will be displayed as additional thin outer arcs. You can define the color by adding `fill` rules for the `cssClass` specified here */
    @Prop() thresholds: Threshold[] = [];

    /** Set to true to use the threshold color for the value arc, ff the value is within one of the defined thresholds. */
    @Prop() useColorFromThresholds: boolean = true;

    /** Customize the margin. You don't have to change them, labels won't be truncated regardless of these values */
    @Prop() margin: ChartMargin = { top: 0, right: 0, bottom: 0, left: 0 };

    /** Can be used to define a custom bar chart root class for styling purposes.*/
    @Prop() rootClass: string = "";

    /** State in which the bar chart is currently in. */
    @Prop() state: ChartState = ChartState.SUCCESS;

    /** Text for displaying when no data is available. */
    @Prop() noDataText: string = "No data available";

    /** Set the transition duration. As long as the transitions do not cause any issues, keep the default */
    @Prop() transitionDurationMs: number = 500;

    /** id attribute for this chart. A unique id will be generated if this is empty */
    @Prop({ mutable: true }) chartId?: string = "";

    @State() transitionProgress = 0;
    @State() valueArc: GaugeArc<number> = undefined;
    @State() dimensions: ChartDimensions = null;

    @Element() el: HTMLUicGaugeChartElement;

    private static startAngle = -110 * (Math.PI / 180);
    private static endAngle = 110 * (Math.PI / 180);

    private thresholdRanges: (Threshold & { nextValue: number })[] = [];

    private readonly debouncedUpdateDimension = debounce(
        () =>
            this.updateDimensions(
                this.el.parentElement.getBoundingClientRect()
            ),
        100
    );

    constructor() {}

    connectedCallback() {
        this.updateDimensions(this.el.parentElement.getBoundingClientRect());
        this.sortAndPairThresholds();
        this.updateData(this.value, undefined);
    }

    @Watch("chartId")
    componentWillLoad() {
        const { chartId } = this;
        this.chartId = chartId || getUniqueId();
    }

    @Watch("value")
    triggerTransition() {
        this.updateData(this.value, this.valueArc);
    }

    /** Sort thresholds and add the nextValue to each item for easier reference as range for rendering.
     * Also add initial and final thresholds ranges by concatenating min and max values.
     * E.g.: [{value: 10}, {value: 50}] => [{value: 0, nextValue: 10}, {value: 10, nextValue: 50}, {value: 50, nextValue: 100}]
     */
    @Watch("thresholds")
    sortAndPairThresholds() {
        this.thresholdRanges = [
            {
                value: this.min,
                cssClass: "threshold-base",
                ignoreThresholdColor: true,
            } as Threshold,
        ]
            .concat(this.thresholds)
            .concat({ value: this.max })
            .sort((a, b) => a.value - b.value)
            .reduce(
                (acc, item, i, arr) =>
                    acc.concat(
                        arr.slice(i + 1, i + 2).map((_item) => ({
                            ...item,
                            nextValue: _item.value,
                        }))
                    ),
                []
            );
    }

    @Listen("resize", { target: "window" })
    onResize() {
        this.debouncedUpdateDimension();
    }

    private updateData(value: number, currentArc: PieArcDatum<number>) {
        this.transitionProgress = 0;

        this.valueArc = this.buildValueArc(value, currentArc);

        selection()
            .transition("arc-transition")
            .duration(this.transitionDurationMs)
            .tween("arc", () => {
                return (t) => (this.transitionProgress = t);
            });
    }

    private updateDimensions(parentBoundingBox) {
        const width = Math.max(parentBoundingBox.width, 10);
        const height = Math.min(width, parentBoundingBox.height);
        const marginWidth = this.margin.left + this.margin.right;
        const marginHeight = this.margin.top + this.margin.bottom;
        // calculate the fraction of the height the radius may take up.
        // Using Math.sin we increase the required height if startAngle or endAngle absolute values are larger,
        // because the height of the arc will be larger then.
        // E.g 90 degree maximum angle will require half the height of 180 degree (0 = vertically upwards from the arc center)
        const radiusMaxHeightFraction = Math.abs(
            0.2 *
                Math.sin(
                    Math.max(
                        Math.abs(GaugeChart.startAngle),
                        Math.abs(GaugeChart.endAngle)
                    ) +
                        Math.PI / 2
                ) +
                0.7
        );

        const radius = Math.min(
            (width - 2 * marginWidth) / 2,
            (height - 2 * marginHeight) * radiusMaxHeightFraction
        );
        // Specify the gauge arc size
        const arcFn = arc<PieArcDatum<number>>()
            .innerRadius(radius * 0.5)
            .outerRadius(radius * 0.8);
        // Another arc that won't be drawn. Just for thresholds
        const thresholdsArcFn = arc<PieArcDatum<number>>()
            .innerRadius(radius * 0.8)
            .outerRadius(radius * 0.83);

        this.dimensions = {
            width,
            height,
            radius,
            arcFn,
            thresholdsArcFn,
            radiusMaxHeightFraction,
        };
    }

    private buildValueArc(
        value: number,
        prev: PieArcDatum<number>
    ): GaugeArc<number> {
        if (value == null) {
            return undefined;
        }

        const arcDatum = {
            data: value,
            value,
            index: 0,
            startAngle: GaugeChart.startAngle,
            endAngle: this.angle(value),
            padAngle: 0,
        };

        return {
            ...arcDatum,
            arcInterpolator: arcInterpolator(arcDatum, prev),
        };
    }

    private angle(value: number) {
        if (typeof value !== "number") {
            return GaugeChart.startAngle;
        }

        if (value > this.max) {
            return GaugeChart.endAngle;
        }

        return (
            GaugeChart.startAngle +
            ((GaugeChart.endAngle - GaugeChart.startAngle) / this.max) * value
        );
    }

    private thresholdClassForValue(value: number) {
        if (!this.useColorFromThresholds || this.thresholds.length === 0) {
            return null;
        }

        let nonExcludedRangeContainingValue = (t) =>
            !t.ignoreThresholdColor && t.value <= value && t.nextValue > value;

        return this.thresholdRanges.find(nonExcludedRangeContainingValue)
            ?.cssClass;
    }

    render() {
        const showNoData =
            this.state === ChartState.NODATA ||
            (this.state !== ChartState.LOADING && this.value == null);
        const displayValue =
            this.value != null
                ? this.valueFormatter(this.value, this.max)
                : "-";

        return (
            <Host>
                {!showNoData && (
                    <div class="uic-gauge-chart__label-container">
                        <div class="uic-gauge-chart__labels">
                            <slot name="value">
                                <span class="uic-gauge-chart__label uic-gauge-chart__label--value">
                                    {displayValue}
                                </span>
                            </slot>
                            <slot name="label">
                                {this.label != null && (
                                    <span class="uic-gauge-chart__label uic-gauge-chart__label--label">
                                        {this.label}
                                    </span>
                                )}
                            </slot>
                        </div>
                    </div>
                )}
                <GaugeChartWrapper
                    chartId={this.chartId}
                    rootClass={this.rootClass}
                    state={this.state}
                    showNoData={showNoData}
                    noDataText={this.noDataText}
                    size={this.dimensions}
                >
                    <g class="uic-gauge-chart__arcs">
                        <GaugeChartArc
                            arcKey="background"
                            arcFn={this.dimensions.arcFn}
                            value={{
                                startAngle: GaugeChart.startAngle,
                                endAngle: GaugeChart.endAngle,
                            }}
                        />
                        {this.valueArc && (
                            <GaugeChartArc
                                arcKey="value"
                                additionalCssClass={this.thresholdClassForValue(
                                    this.value
                                )}
                                pathInterpolator={this.valueArc.arcInterpolator}
                                t={this.transitionProgress}
                                arcFn={this.dimensions.arcFn}
                            />
                        )}
                        {this.thresholds.length > 0 && (
                            <g class="uic-gauge-chart__arcs--thresholds">
                                {this.thresholdRanges.map((t) => (
                                    <GaugeChartArc
                                        arcKey={`threshold`}
                                        additionalCssClass={t.cssClass}
                                        arcFn={this.dimensions.thresholdsArcFn}
                                        value={{
                                            startAngle: this.angle(t.value),
                                            endAngle: this.angle(t.nextValue),
                                        }}
                                    />
                                ))}
                            </g>
                        )}
                    </g>
                </GaugeChartWrapper>
            </Host>
        );
    }
}
